import { useApi } from '@classgo/app-kit';
import { Select } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { listQuery } from '../../academic/api';
import { EntityList } from '../../components/entity-list';
import { GradeForm, LevelForm } from '../../forms/simple-forms';
import { useT } from '../../i18n';

/**
 * Levels and, below, the grades of the selected level.
 */
export const LevelsScreen = () => {
  const t = useT();
  const api = useApi();
  const levels = useQuery(listQuery(api, 'levels'));
  const sortedLevels = [...(levels.data?.items ?? [])].sort((a, b) => a.position - b.position);
  const [levelId, setLevelId] = useState('');
  useEffect(() => {
    if (!levelId && sortedLevels[0]) setLevelId(sortedLevels[0].id);
    if (levelId && !sortedLevels.some((level) => level.id === levelId))
      setLevelId(sortedLevels[0]?.id ?? '');
  }, [levelId, sortedLevels]);
  const grades = useQuery({ ...listQuery(api, 'grades', { levelId }), enabled: levelId !== '' });
  const nextGrade =
    (Math.max(0, ...(grades.data?.items.map((grade) => grade.position) ?? [])) || 0) + 1;

  return (
    <div className="space-y-4">
      <EntityList
        entity="levels"
        title={t('levels.title')}
        caption={t('levels.caption')}
        createLabel={t('levels.new')}
        editTitle={t('levels.edit')}
        emptyTitle={t('levels.empty.title')}
        emptyBody={t('levels.empty.body')}
        columns={[
          {
            key: 'position',
            header: t('levels.column.position'),
            cell: (row) => row.position,
            sortable: true,
            sortValue: (row) => row.position,
          },
          {
            key: 'name',
            header: t('levels.column.name'),
            cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>,
          },
        ]}
        renderForm={({ current, onDone }) => (
          <LevelForm
            nextPosition={(sortedLevels.at(-1)?.position ?? 0) + 1}
            current={current}
            onDone={onDone}
          />
        )}
      />
      <EntityList
        entity="grades"
        query={{ levelId }}
        title={t('grades.title')}
        caption={t('grades.caption')}
        createLabel={t('grades.new')}
        editTitle={t('grades.edit')}
        emptyTitle={t('grades.empty.title')}
        emptyBody={sortedLevels.length === 0 ? t('grades.noLevel') : t('grades.empty.body')}
        toolbar={
          <Select
            label={t('grades.filter.level')}
            value={levelId}
            onChange={(event) => setLevelId(event.target.value)}
            options={sortedLevels.map((level) => ({ value: level.id, label: level.name }))}
            placeholder={sortedLevels.length === 0 ? t('grades.noLevel') : undefined}
          />
        }
        columns={[
          {
            key: 'position',
            header: t('grades.column.position'),
            cell: (row) => row.position,
            sortable: true,
            sortValue: (row) => row.position,
          },
          {
            key: 'name',
            header: t('grades.column.name'),
            cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>,
          },
        ]}
        renderForm={({ current, onDone }) => (
          <GradeForm
            levels={sortedLevels}
            levelId={levelId}
            nextPosition={nextGrade}
            current={current}
            onDone={onDone}
          />
        )}
      />
    </div>
  );
};
